(()=>{
    const navigation = document.querySelector('.research-subnav');
    const gallery = document.querySelector('[data-research-page]');
    if (!navigation || !gallery) return;
    const links = [
        ...navigation.querySelectorAll('[data-research-link]')
    ];
    let hash = window.location.hash.slice(1);
    try {
        hash = decodeURIComponent(hash);
    } catch  {}
    const previousChapter = document.getElementById(hash)?.closest('[data-print-only]')?.id || (/^(?:krea-2|moodboard-)/.test(hash) ? 'krea-2' : null);
    const destination = links.find((link)=>link.dataset.researchLink === previousChapter);
    if (destination && destination.getAttribute('aria-current') !== 'page') {
        const url = new URL(destination.href);
        url.hash = window.location.hash;
        window.location.replace(url.href);
        return;
    }
    // File previews and browsers without fetch retain real document navigation.
    if (!/^https?:$/.test(window.location.protocol) || !window.fetch || !window.history?.pushState) return;
    const cache = new Map();
    let revision = 0, controller, indicatorAnimation, wireAnimations = [], changing = false, scrollTimer;
    const key = (url)=>new URL(url, window.location.href).pathname.replace(/\.html$/, '').replace(/\/$/, '');
    const state = ()=>({
            ...history.state,
            researchGallery: {
                url: window.location.href,
                scroll: window.scrollY
            }
        });
    history.replaceState(state(), '', window.location.href);
    history.scrollRestoration = 'manual';
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const stopIndicator = ()=>indicatorAnimation?.cancel();
    reducedMotion.addEventListener('change', ()=>{
        stopIndicator();
        if (reducedMotion.matches) cancelWireFade();
    });
    window.addEventListener('resize', stopIndicator, {
        passive: true
    });
    const status = document.createElement('span');
    status.className = 'research-navigation-status';
    status.setAttribute('role', 'status');
    navigation.append(status);
    async function load(url, signal) {
        const id = key(url);
        if (cache.has(id)) return cache.get(id);
        const response = await fetch(url, {
            signal,
            credentials: 'same-origin',
            headers: {
                Accept: 'text/html'
            }
        });
        if (!response.ok || new URL(response.url).origin !== window.location.origin) throw Error('Gallery request failed');
        const incoming = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = incoming.querySelector('[data-research-page]');
        if (!root || !links.some((link)=>link.dataset.researchLink === root.dataset.researchPage) || !root.querySelector('main')) throw Error('Not a gallery document');
        // Resolve assets before moving nodes into a document whose URL may differ.
        for (const node of incoming.querySelectorAll('[src], [href], [poster]'))for (const attr of [
            'src',
            'href',
            'poster'
        ]){
            const value = node.getAttribute(attr);
            if (value && !value.startsWith('#') && !value.startsWith('url:')) node.setAttribute(attr, new URL(value, response.url).href);
        }
        cache.set(id, incoming);
        return incoming;
    }
    function saveScroll() {
        if (!changing) history.replaceState(state(), '', window.location.href);
    }
    window.addEventListener('scroll', ()=>{
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(saveScroll, 120);
    }, {
        passive: true
    });
    function cancelWireFade() {
        wireAnimations.forEach((animation)=>animation.cancel());
        wireAnimations = [];
    }
    async function navigate(url, { pop = false, scroll = null } = {}) {
        const request = ++revision;
        controller?.abort();
        controller = new AbortController();
        cancelWireFade();
        clearTimeout(scrollTimer);
        if (!pop) saveScroll();
        changing = true;
        const pinned = navigation.getBoundingClientRect().top <= 1;
        const oldScroll = window.scrollY;
        navigation.setAttribute('aria-busy', 'true');
        try {
            const incoming = await load(url, controller.signal);
            if (request !== revision) return;
            const nextRoot = incoming.querySelector('[data-research-page]');
            const nextMain = nextRoot.querySelector('main').cloneNode(true);
            const nextHero = nextRoot.querySelector('.gallery-masthead .chapter-hero')?.cloneNode(true);
            if (!nextHero) throw Error('Missing gallery masthead');
            const nextDialog = incoming.querySelector('.moodboard-lightbox')?.cloneNode(true);
            const swap = ()=>{
                if (request !== revision) return;
                document.dispatchEvent(new Event('research:before-swap'));
                gallery.querySelector('main').replaceWith(nextMain);
                gallery.querySelector('.gallery-masthead .chapter-hero').replaceWith(nextHero);
                wireAnimations = [];
                document.querySelector('.moodboard-lightbox')?.remove();
                if (nextDialog) document.body.append(nextDialog);
                gallery.dataset.researchPage = nextRoot.dataset.researchPage;
                for (const link of links){
                    const active = link.dataset.researchLink === nextRoot.dataset.researchPage;
                    if (active) link.setAttribute('aria-current', 'page');
                    else link.removeAttribute('aria-current');
                }
                const indicator = navigation.querySelector('.research-tab-indicator');
                const before = indicator.getBoundingClientRect();
                indicatorAnimation?.cancel();
                links.find((link)=>link.getAttribute('aria-current') === 'page').append(indicator);
                const after = indicator.getBoundingClientRect();
                if (!reducedMotion.matches && after.width > 0 && indicator.animate) indicatorAnimation = indicator.animate([
                    {
                        transform: `translateX(${before.left - after.left}px) scaleX(${before.width / after.width})`
                    },
                    {
                        transform: 'translateX(0) scaleX(1)'
                    }
                ], {
                    duration: 220,
                    easing: 'cubic-bezier(.2,.75,.25,1)'
                });
                document.title = incoming.title;
                for (const selector of [
                    'meta[name="description"]',
                    'meta[property="og:title"]',
                    'meta[property="og:description"]'
                ]){
                    const next = incoming.querySelector(selector);
                    if (next) document.querySelector(selector)?.setAttribute('content', next.content);
                }
                if (!pop) history.pushState({
                    researchGallery: {
                        url,
                        scroll: 0
                    }
                }, '', url);
                document.dispatchEvent(new Event('research:after-swap'));
                const target = scroll ?? (pinned ? window.scrollY + navigation.getBoundingClientRect().top : oldScroll);
                window.scrollTo({
                    top: target,
                    left: 0,
                    behavior: 'instant'
                });
                links.find((link)=>link.getAttribute('aria-current') === 'page').focus({
                    preventScroll: true
                });
                status.textContent = `${links.find((link)=>link.getAttribute('aria-current') === 'page').textContent.trim()} loaded`;
            };
            if (!reducedMotion.matches) {
                const wires = gallery.querySelectorAll('.gallery-masthead .research-hero-wires, .gallery-masthead .research-hero-overlap');
                wireAnimations = [
                    ...wires
                ].filter((node)=>node.animate).map((node)=>node.animate([
                        {
                            opacity: getComputedStyle(node).opacity
                        },
                        {
                            opacity: 0
                        }
                    ], {
                        duration: 180,
                        easing: 'ease-in',
                        fill: 'forwards'
                    }));
                await Promise.all(wireAnimations.map((animation)=>animation.finished?.catch(()=>{})));
            }
            if (request !== revision) return;
            swap();
            if (!reducedMotion.matches) nextMain.animate?.([
                {
                    opacity: .55
                },
                {
                    opacity: 1
                }
            ], {
                duration: 140,
                easing: 'ease-out'
            });
            if (request === revision) {
                changing = false;
                saveScroll();
            }
        } catch (error) {
            if (request === revision && error.name !== 'AbortError') {
                cancelWireFade();
                window.location.assign(url);
            }
        } finally{
            if (request === revision) {
                changing = false;
                navigation.removeAttribute('aria-busy');
            }
        }
    }
    navigation.addEventListener('click', (event)=>{
        const link = event.target.closest('[data-research-link]');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download')) return;
        if (new URL(link.href).origin !== window.location.origin) return;
        event.preventDefault();
        if (link.getAttribute('aria-current') === 'page') {
            revision++;
            controller?.abort();
            cancelWireFade();
            navigation.removeAttribute('aria-busy');
            changing = false;
            return;
        }
        navigate(link.href);
    });
    window.addEventListener('popstate', (event)=>{
        const target = event.state?.researchGallery;
        if (!target) {
            window.location.reload();
            return;
        }
        navigate(window.location.href, {
            pop: true,
            scroll: target.scroll || 0
        });
    });
})();

//# sourceMappingURL=resume-research.687c0e7a.js.map
